import router from "./router";
import constant from "./main.constants";
import VueI18n from "@/plugins/vue-i18n";
import moment from "moment";
import "moment/locale/tr";
import "moment/locale/ru";
import "moment/locale/ar";
import "moment/locale/de";
import "moment/locale/ro";
import "moment/locale/az";
import Exceljs from "exceljs";
import saveAs from "file-saver";
import { drawPdfWatermark } from "@/helpers/pdfDownloadHelper";
import tableHelper from "./helpers/tableHelper";
import LZString from "lz-string";
import DOMPurify from "dompurify";
import { limitRows } from "./helpers/excelRowLimitHelper";

const IMAGE_SIZE_TIMEOUT = 15000;

let commonFunc = {
  prepareUniqueValueForNode(nodeType, nodeId) {
    let uniqueId = nodeId
      .toString()
      .replace(/ /g, "")
      .replace(/\./g, "")
      .replace(/\:/g, "")
      .replace(/\,/g, "");

    let uniqueType = nodeType
      .toString()
      .replace(/ /g, "")
      .replace(/\./g, "")
      .replace(/\:/g, "")
      .replace(/\,/g, "");
    return uniqueType + "_" + uniqueId;
  },
  prepareUniqueValueForLink(linkId) {
    let uniqueId = linkId
      .toString()
      .replace(/ /g, "")
      .replace(/\./g, "")
      .replace(/\:/g, "")
      .replace(/\,/g, "");
    return uniqueId;
  },
  formatDateTime(execTime) {
    let date = new Date(execTime);

    let day = convertTo2Chars(date.getDate());
    let month = convertTo2Chars(date.getMonth() + 1);

    let hh = convertTo2Chars(date.getHours());
    let mm = convertTo2Chars(date.getMinutes());
    let ss = convertTo2Chars(date.getSeconds());

    return (
      day +
      "." +
      month +
      "." +
      date.getFullYear() +
      " " +
      hh +
      ":" +
      mm +
      ":" +
      ss
    );

    /////
    function convertTo2Chars(singleChar) {
      if (singleChar < 10) {
        singleChar = "0" + singleChar;
      }
      return singleChar;
    }
  },
  formatDate(execTime) {
    let date = new Date(execTime);
    let day = convertTo2Chars(date.getDate());
    let month = convertTo2Chars(date.getMonth() + 1);

    return day + "." + month + "." + date.getFullYear();

    /////
    function convertTo2Chars(singleChar) {
      if (singleChar < 10) {
        singleChar = "0" + singleChar;
      }
      return singleChar;
    }
  },
  // KYC/CDD: UTC-aware, locale-formatted variants. Use these where the backend sends
  // UTC datetimes with a "Z" suffix. Kept separate from formatDateTime/formatDate so the
  // rest of the app keeps its original local-time dd.MM.yyyy behaviour (≈198 call sites).
  formatDateTimeUtc(execTime) {
    if (!execTime) return "";
    // Safety net: if the server sends a string without timezone info, treat it as UTC.
    const str =
      typeof execTime === "string" &&
        !execTime.endsWith("Z") &&
        !/[+-]\d{2}:?\d{2}$/.test(execTime)
        ? execTime + "Z"
        : execTime;
    const date = new Date(str);
    if (isNaN(date.getTime())) return String(execTime);
    return date.toLocaleString(navigator.language || "en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  },
  formatDateUtc(execTime) {
    if (!execTime) return "";
    const str =
      typeof execTime === "string" &&
        !execTime.endsWith("Z") &&
        !/[+-]\d{2}:?\d{2}$/.test(execTime)
        ? execTime + "Z"
        : execTime;
    const date = new Date(str);
    if (isNaN(date.getTime())) return String(execTime);
    return date.toLocaleDateString(navigator.language || "en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  },
  dateFormat: function (dateValue) {
    let myDate;
    let dateArr = dateValue.split("-");
    if (dateArr.length > 2) {
      myDate = dateArr[2] + "." + dateArr[1] + "." + dateArr[0];
    } else {
      myDate = dateValue;
    }
    return myDate;
  },
  parseExcelDateString(value) {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value !== "string") {
      return null;
    }
    const datetimeMatch = value.match(
      /^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/,
    );
    if (datetimeMatch) {
      const day = parseInt(datetimeMatch[1], 10);
      const month = parseInt(datetimeMatch[2], 10) - 1;
      const year = parseInt(datetimeMatch[3], 10);
      const hour = parseInt(datetimeMatch[4], 10);
      const minute = parseInt(datetimeMatch[5], 10);
      const second = datetimeMatch[6] ? parseInt(datetimeMatch[6], 10) : 0;
      const parsedDate = new Date(
        Date.UTC(year, month, day, hour, minute, second),
      );
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
    const dateMatch = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (dateMatch) {
      const day = parseInt(dateMatch[1], 10);
      const month = parseInt(dateMatch[2], 10) - 1;
      const year = parseInt(dateMatch[3], 10);
      const parsedDate = new Date(Date.UTC(year, month, day));
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
    const isoDate = new Date(value);
    return isNaN(isoDate.getTime()) ? null : isoDate;
  },
  formatDateFibaGet(date) {
    if (date && date.length === 8) {
      const year = date.substring(0, 4);
      const month = date.substring(4, 6);
      const day = date.substring(6, 8);
      return `${day}.${month}.${year}`;
    }
  },
  formatDateFibaPost(date) {
    if (date) {
      const [day, month, year] = date.split(".");
      return `${year}${month}${day}`;
    }
  },
  timeFormat(dateValue, timeValue) {
    return dateValue + " " + timeValue + ":00";
  },
  datePickerFormat: function (dateValue) {
    let myDate;
    let dateArr = dateValue.split(".");
    myDate = dateArr[2] + "-" + dateArr[1] + "-" + dateArr[0];
    return myDate;
  },
  dateTimePickerFormat(dateAndTimeValue) {
    let myDate, myTime;
    let dateAndTime = dateAndTimeValue.split(" ");
    let dateValue = dateAndTime[0];
    let timeValue = dateAndTime[1];
    let dateArr = dateValue.split(".");
    myDate = dateArr[2] + "-" + dateArr[1] + "-" + dateArr[0];
    myTime = timeValue ? timeValue.substring(0, 5) : "00:00";
    return { date: myDate, time: myTime };
  },
  dateFormatToYYYYMMDD(dateValue) {
    const onlyDate = dateValue.split("T")[0];

    return onlyDate;
  },
  dbDateToTrDate(dateValue) {
    if (dateValue) {
      const dateAndTime = dateValue.split("T");
      if (dateAndTime.length > 1) {
        const dateParts = dateAndTime[0].split("-");
        if (dateParts.length > 2) {
          const timeParts = dateAndTime[1].split(".");
          if (timeParts.length > 1) {
            return (
              dateParts[2] +
              "." +
              dateParts[1] +
              "." +
              dateParts[0] +
              " " +
              timeParts[0]
            );
          } else {
            return (
              dateParts[2] +
              "." +
              dateParts[1] +
              "." +
              dateParts[0] +
              " " +
              dateAndTime[1]
            );
          }
        } else {
          return "";
        }
      } else {
        return dateValue;
      }
    } else {
      return "";
    }
  },
  dbDateToTrDateWithoutTime(dateValue) {
    if (dateValue) {
      let dateAndTime = dateValue.split("T");
      if (dateAndTime.length > 1) {
        let dateParts = dateAndTime[0].split("-");
        if (dateParts.length > 2) {
          let trDateAndTime =
            dateParts[2] + "." + dateParts[1] + "." + dateParts[0];
          return trDateAndTime;
        } else {
          return "";
        }
      } else {
        return "";
      }
    } else {
      return "";
    }
  },
  dateToTrDateWithoutTime(dateValue) {
    if (dateValue) {
      const dateParts = dateValue.split("-");
      if (dateParts.length > 2) {
        return `${dateParts[2]}.${dateParts[1]}.${dateParts[0]}`;
      } else {
        return "";
      }
    } else {
      return "";
    }
  },
  replaceAllWithoutReplaceAll(item, from, to) {
    let lastWord = "";
    let index = 0;
    let fromCurrentIndex = 0;
    let convertedWord = "";
    while (index < item.length) {
      if (item[index] == from[fromCurrentIndex]) {
        lastWord += from[fromCurrentIndex];
        if (lastWord == from) {
          convertedWord += to;
          lastWord = "";
          fromCurrentIndex = 0;
        } else {
          fromCurrentIndex++;
        }
      } else {
        convertedWord += lastWord;
        convertedWord += item[index];
        lastWord = "";
        fromCurrentIndex = 0;
      }
      index++;
    }

    if (lastWord != "") {
      convertedWord += lastWord;
    }

    return convertedWord;
  },
  groupBy(input, key) {
    return input.reduce((acc, currentValue) => {
      const groupKey = currentValue[key];
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(currentValue);
      return acc;
    }, {});
  },
  getRandomColor() {
    let letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  },
  getRouteColor(routeIndex) {
    const palette = ["red", "blue", "purple", "orange", "teal"];
    return routeIndex < palette.length
      ? palette[routeIndex]
      : this.getRandomColor();
  },
  sortEntities(entitiesWithAttributes) {
    entitiesWithAttributes.sort(function (a, b) {
      if (a.entityOrder && b.entityOrder) {
        return a.entityOrder - b.entityOrder;
      } else if (a.entityOrder) {
        return -1;
      } else if (b.entityOrder) {
        return 1;
      } else {
        return 0;
      }
    });
    return entitiesWithAttributes;
  },
  pastedOnTextArea(e, attr, myThis) {
    e.preventDefault();
    attr.operator = "in";
    attr.value = "";
    attr.similarityPercentage = undefined;
    attr.isOperatorSimilar = false;
    let newText = "";
    let activeEl = document.activeElement;
    let textStart = activeEl.value.slice(0, activeEl.selectionStart);
    let textEnd = activeEl.value.slice(
      activeEl.selectionEnd,
      activeEl.value.length,
    );

    let pastedArr = e.clipboardData.getData("text/plain").split("\n");
    pastedArr.forEach((arr, i) => {
      if (arr != "") {
        if (arr[arr.length - 1] == "\r") {
          newText += arr.substring(0, arr.length - 1);
        } else if (arr[0] == "\r") {
          newText += arr.substring(1, arr.length);
        } else {
          newText += arr.substring(0, arr.length);
        }

        if (i != pastedArr.length - 1) newText += ",";
      }
    });
    if (newText[0] == ",") {
      newText = newText.substring(1, newText.length);
    }
    if (newText[newText.length - 1] == ",") {
      newText = newText.substring(0, newText.length - 1);
    }

    if (textStart[textStart.length - 1] == "," && textStart != "") {
      textStart = textStart.substring(0, textStart.length - 1);
    }
    if (textStart == "" && textEnd == "") {
      attr.value = newText;
    } else {
      if (textStart != "") {
        attr.value += textStart + "," + newText + textEnd;
      } else {
        attr.value += newText + textEnd;
      }
    }

    if (myThis.singleQueryMode) {
      if (myThis.singleQueryMode == 2) {
        myThis.nodeEdgeMapWithAttrs = JSON.parse(
          JSON.stringify(myThis.nodeEdgeMapWithAttrs),
        );
      } else {
        myThis.selectedEntityAttrs = JSON.parse(
          JSON.stringify(myThis.selectedEntityAttrs),
        );
      }
    }
  },
  //TODO ktolga: coğrafi analiz içerisinden de bu fonksiyon çağırılmalı
  operatorChange(attr, myThis) {
    if (attr.operator == "between") {
      if (attr.timeValue == null && attr.dataType == "Datetime") {
        attr.timeValue = "00:00";
      }
      attr.isBetween = true;
      attr.isLastDate = false;
      if (attr.dataType == "Datetime") {
        attr.timeValueBetween = "23:59";
      }
      attr.textDisabled = false;
      attr.isOperatorSimilar = false;
      attr.similarityPercentage = undefined;
    } else if (
      attr.operator == "is null" ||
      attr.operator == "is not null" ||
      attr.operator == "previous week" ||
      attr.operator == "previous month"
    ) {
      attr.textDisabled = true;
      attr.isLastDate = false;
      attr.isBetween = false;
      attr.value = undefined;
      attr.valueMax = undefined;
      attr.isOperatorSimilar = false;
      attr.similarityPercentage = undefined;
    } else if (
      attr.operator == "last day" ||
      attr.operator == "last month" ||
      attr.operator == "last year" ||
      attr.operator == "last hour" ||
      attr.operator == "last minute"
    ) {
      attr.textDisabled = false;
      attr.isBetween = false;
      attr.value = undefined;
      attr.valueMax = undefined;
      attr.isLastDate = true;
      attr.isOperatorSimilar = false;
      attr.similarityPercentage = undefined;
    } else if (attr.operator == "similar") {
      attr.isOperatorSimilar = true;
      attr.isBetween = false;
      attr.valueMax = undefined;
      attr.isLastDate = false;
      attr.textDisabled = false;
    } else {
      if (attr.timeValue == null && attr.dataType == "Datetime") {
        attr.timeValue = "00:00";
      }
      attr.isBetween = false;
      attr.valueMax = undefined;
      attr.isLastDate = false;
      attr.textDisabled = false;
      attr.isOperatorSimilar = false;
      attr.similarityPercentage = undefined;
    }

    if (myThis.singleQueryMode == 2) {
      myThis.nodeEdgeMapWithAttrs = JSON.parse(
        JSON.stringify(myThis.nodeEdgeMapWithAttrs),
      );
    } else {
      myThis.selectedEntityAttrs = JSON.parse(
        JSON.stringify(myThis.selectedEntityAttrs),
      );
    }
  },
  prepareSingleQueryFilters(entityAttrs, filters, myThis) {
    let commonFuncThis = this;

    entityAttrs.forEach((filter) => {
      let filterValue,
        filterMaxValue,
        parameterDatatype = null;
      if (
        (filter.value != undefined &&
          filter.value != null &&
          filter.value != "") ||
        filter.textDisabled
      ) {
        if (!filter.textDisabled) {
          if (filter.dataType == "Date") {
            if (!filter.isLastDate) {
              filterValue = commonFuncThis.dateFormat(filter.value);

              if (filter.operator == "between") {
                if (filter.valueMax) {
                  filterMaxValue = commonFuncThis.dateFormat(filter.valueMax);
                }
              }
            } else {
              filterValue = filter.value;
            }
          } else if (filter.dataType == "Datetime") {
            if (!filter.isLastDate) {
              filterValue = commonFuncThis.dateTimeFormat(
                filter.value,
                filter.timeValue,
              );

              if (filter.operator == "between") {
                if (filter.valueMax) {
                  filterMaxValue = this.dateTimeFormat(
                    filter.valueMax,
                    filter.timeValueBetween,
                  );
                }
              }
            } else {
              filterValue = filter.value;
            }
          } else if (filter.dataType == "Parameter") {
            parameterDatatype = commonFuncThis.addParameterDatatypeToFilter(
              myThis,
              filter,
            );
            filterValue = filter.value.join("#!#");
          } else if (filter.name == "sanctionorpepinput") {
            filterValue = commonFuncThis.getPepAndSanctionMapValue(
              filter.value,
            );
          } else {
            filterValue = filter.value;

            if (filter.operator == "between") {
              filterMaxValue = filter.valueMax;
            }
          }
        } else {
          filterValue = "";
        }

        if (filter.textDisabled) {
          filters[filter.name] = [];
          filters[filter.name].push({
            Operator: filter.operator,
            Type: filter.dataType,
          });
        } else {
          let checkValue = filterValue.replace(/ /g, "");
          if (checkValue != "") {
            filters[filter.name] = [];

            filters[filter.name].push({
              FilterType:
                filter.dataType == "Parameter"
                  ? constant.FILTER_TYPES.parameter
                  : constant.FILTER_TYPES.static,
              Value: filterValue,
              ValueMax: filterMaxValue,
              Operator: filter.operator,
              Type: filter.dataType,
              CaseSensitive: filter.caseSensitive,
              LanguageSensitive: filter.languageSensitive,
              ParameterDatatype: parameterDatatype,
              SimilarityPercentage: filter.similarityPercentage,
            });
          }
        }
        if (myThis.complexQueryFilters) {
          if (!myThis.complexQueryFilters[filter.name]) {
            myThis.complexQueryFilters[filter.name] = [];
          }
          let isFilterDuplicated = false;
          myThis.complexQueryFilters[filter.name].forEach((filterControl) => {
            if (filterControl.Value == filterValue) {
              isFilterDuplicated = true;
            }
          });
          if (!isFilterDuplicated) {
            myThis.complexQueryFilters[filter.name].push({
              FilterType:
                filter.dataType == "Parameter"
                  ? constant.FILTER_TYPES.parameter
                  : constant.FILTER_TYPES.static,
              Value: filterValue,
              ValueMax: filterMaxValue,
              Operator: filter.operator,
              Type: filter.dataType,
              CaseSensitive: filter.caseSensitive,
              LanguageSensitive: filter.languageSensitive,
              ParameterDatatype: parameterDatatype,
              SimilarityPercentage: filter.similarityPercentage,
            });
          }
        }
      }
    });
  },
  addParameterDatatypeToFilter(myThis, filter) {
    let parameterSourceAttrDatatype = myThis.entitiesWithAttributes
      .find((entity) => {
        return entity.id == filter.fillentityid;
      })
      .attributeList.find((attr) => {
        return attr.entityAttributeId == filter.valueentityattributeid;
      })?.datatype;
    return parameterSourceAttrDatatype;
  },
  dateFormat: function (dateValue) {
    let myDate;
    let dateArr = dateValue.split("-");
    if (dateArr.length > 2) {
      myDate = dateArr[2] + "." + dateArr[1] + "." + dateArr[0];
    } else {
      myDate = dateValue;
    }
    return myDate;
  },
  dateTimeFormat(dateValue, timeValue) {
    let myDate = this.dateFormat(dateValue);
    let HHmm = timeValue ? timeValue : "00:00";

    return myDate + " " + HHmm + ":00";
  },
  logOut(isTokenExpired, skipRedirect) {
    let localStorageItems = constant.LOCAL_STORAGE_ITEMS;
    let sessionStorageItems = constant.SESSION_STORAGE_ITEMS;

    Object.keys(localStorageItems).forEach((item) => {
      if (!constant.DONT_REMOVE_LOCALE_STORAGE_ITEMS[item]) {
        localStorage.removeItem(localStorageItems[item]);
      }
    });

    Object.keys(sessionStorageItems).forEach((item) => {
      sessionStorage.removeItem(sessionStorageItems[item]);
    });

    //TODO: cookieye göre işlem yapılırsa bu satır açılmalı
    //this.deleteCookies();

    // Hard refresh ile login sayfasina yönlendirr (logout ya da expire oluncamemory release olmuyordu)
    if (!skipRedirect && router.history.current.path != "/login") {
      const loginHash = isTokenExpired
        ? "#/login?type=tokenExpired"
        : "#/login";
      window.location.hash = loginHash;
      window.location.reload();
    }
  },
  getCookieValue(name) {
    return (
      document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")?.pop() ||
      ""
    );
  },
  deleteCookies() {
    document.cookie.split(";").forEach(function (c) {
      document.cookie =
        c.trim().split("=")[0] +
        "=;" +
        "expires=Thu, 01 Jan 1970 00:00:00 UTC;";
    });
  },
  checkEntityPermissionWithName(entityName) {
    if (localStorage.entityNameEntityIdMap) {
      let permList = localStorage.perms.split(";");
      let entityId = JSON.parse(localStorage.entityNameEntityIdMap)[entityName];
      return permList.indexOf("perm_entity_" + entityId) > -1;
    } else {
      this.errorHandler("metaDataLoadError");
    }
  },
  checkPermissionWithName(permName) {
    if (localStorage.perms) {
      let permList = localStorage.perms.split(";");
      return permList.indexOf(permName) > -1;
    }
    return false;
  },

  // Fraud sayfaları Akşit modülünde de aynı component'lerle kullanılıyor.
  // Yönlendirmeler bulunulan modülün (fraud/aksit) route'una yapılmalıdır.
  isAksitRoute(route) {
    return route.path.startsWith("/aksit");
  },
  resolveModuleRouteName(route, fraudRouteName) {
    if (commonFunc.isAksitRoute(route)) {
      return (
        constant.FRAUD_TO_AKSIT_ROUTE_NAMES[fraudRouteName] ?? fraudRouteName
      );
    }
    return fraudRouteName;
  },
  resolveModulePath(route, fraudPath) {
    if (commonFunc.isAksitRoute(route)) {
      return fraudPath.replace("/fraud/", "/aksit/");
    }
    return fraudPath;
  },

  // EXAMPLE
  // en =>  "yearsOld": "I'm {0} years old, I live in {1}",
  // tr => "yearsOld": "{0} yaşındayım, {1}da yaşıyorum",
  // call => languageLocalize("yearsOld", 21, "İstanbul")
  // "12.05.2026 14:30 · 3 ay önce" gösteriminin göreli yarısı. ISO ve modülün
  // bilinen görüntü formatlarını kabul eder; çözülemeyen değerde boş döner ki
  // tablo mutlak tarihi tek başına göstermeye devam etsin.
  formatRelativeDate(dateValue) {
    if (!dateValue) {
      return "";
    }
    const lang = (localStorage.preferredLanguage || "tr").toLowerCase();
    const parsed = moment(
      dateValue,
      [
        moment.ISO_8601,
        "DD.MM.YYYY HH:mm:ss",
        "DD.MM.YYYY HH:mm",
        "DD.MM.YYYY"
      ],
      true
    );
    return parsed.isValid() ? parsed.locale(lang).fromNow() : "";
  },
  localize(localizeKey, ...args) {
    let translatedWord = "";
    if (VueI18n.te("KEYS." + localizeKey)) {
      translatedWord = VueI18n.t("KEYS." + localizeKey);
    } else {
      return localizeKey;
    }
    const parameters = [];
    args.forEach((arg) => {
      if (arg !== null && arg !== undefined) {
        if (VueI18n.te("KEYS." + arg)) {
          const translatedArg = VueI18n.t("KEYS." + arg);
          parameters.push(translatedArg);
        } else {
          parameters.push(arg);
        }
      }
    });

    return commonFunc.formatString(translatedWord, ...parameters);
  },
  hasAlert(localizeKey) {
    return VueI18n.te("ALERTS." + localizeKey);
  },
  localizeAlert(localizeKey, ...args) {
    const translatedWord = VueI18n.t("ALERTS." + localizeKey);
    if (translatedWord.startsWith("ALERTS.")) {
      return localizeKey;
    }
    return commonFunc.formatString(translatedWord, ...args);
  },
  // Login hata kodlarini (server: LoginErrorCodes.cs) localization key'lerine cevirir.
  // Kod eslesmezse null doner; login sayfalari eski fallback dallarina devam eder.
  resolveLoginErrorMessage(errorCode, ...args) {
    const keyMap = {
      LGN_1001: "loginInvalidCredentials",
      LGN_1002: "loginUserBlocked",
      LGN_1003: "loginUserPassivePendingApproval",
      LGN_1004: "loginUserNotProvisioned",
      LGN_1005: "loginNoPermission",
      LGN_2001: "loginTwoFactorCodeInvalid",
      LGN_2002: "loginTwoFactorAttemptLimit",
      LGN_2003: "loginZaptiyeSmsRateLimited",
      LGN_2004: "loginZaptiyeSmsSendFailed",
      LGN_9001: "loginTechnicalError",
      LGN_9002: "loginLdapUserFetchFailed",
    };
    const localizeKey = keyMap[errorCode];
    if (!localizeKey) {
      return null;
    }
    return commonFunc.localize(localizeKey, ...args);
  },
  // Teknik login hatalari (LGN_9xxx) kirmizi error toast ile gosterilir
  isTechnicalLoginError(errorCode) {
    return !!errorCode && errorCode.startsWith("LGN_9");
  },
  resolveDbErrorMessage(typeCode, traceId, editorContext = false) {
    const keyMap = {
      Timeout: "dbErrorTimeout",
      ConnectionFailure: "dbErrorConnectionFailure",
      PermissionDenied: "dbErrorPermissionDenied",
      ObjectNotFound: "dbErrorObjectNotFound",
      Syntax: editorContext ? "dbErrorSyntaxEditor" : "dbErrorSyntax",
      ConstraintViolation: "dbErrorConstraintViolation",
      DataTypeMismatch: "dbErrorDataTypeMismatch",
      Cancelled: "dbErrorCancelled",
      Unknown: "dbErrorUnknown",
    };
    const localizeKey = keyMap[typeCode] || "dbErrorUnknown";
    const message = commonFunc.localize(localizeKey);
    if (traceId) {
      return `${message} ${commonFunc.localize("dbErrorSupportCode", traceId)}`;
    }
    return message;
  },
  resolveWebServiceErrorMessage(typeCode, traceId) {
    const keyMap = {
      Timeout: "wsErrorTimeout",
      ConnectionFailure: "wsErrorConnectionFailure",
      ServiceUnavailable: "wsErrorServiceUnavailable",
      Unauthorized: "wsErrorUnauthorized",
      NotFound: "wsErrorNotFound",
      InvalidResponse: "wsErrorInvalidResponse",
      ServiceBusinessError: "wsErrorServiceBusinessError",
      Unknown: "wsErrorUnknown",
    };
    const localizeKey = keyMap[typeCode] || "wsErrorUnknown";
    const message = commonFunc.localize(localizeKey);
    if (traceId) {
      return `${message} ${commonFunc.localize("dbErrorSupportCode", traceId)}`;
    }
    return message;
  },
  hasClassifiedQueryError(responseData) {
    if (!responseData) {
      return false;
    }
    return !!(
      responseData.databaseErrorTypeCode ||
      responseData.webServiceErrorTypeCode ||
      responseData.errorListMessage
    );
  },
  isRequestTimeoutError(error) {
    if (!error) {
      return false;
    }
    const errorCode = error.code || (error.response && error.response.code);
    if (errorCode === "ECONNABORTED" || errorCode === "ETIMEDOUT") {
      return true;
    }
    const status = error.status || (error.response && error.response.status);
    if (status === 408 || status === 504 || status === 524) {
      return true;
    }
    const message = typeof error.message === "string" ? error.message : "";
    return message.toLowerCase().includes("timeout");
  },
  resolveRequestErrorMessage(error, editorContext = false) {
    if (!error) {
      return null;
    }
    if (commonFunc.isRequestTimeoutError(error)) {
      return commonFunc.localize("requestTimeout");
    }
    const responseData = error.data || (error.response && error.response.data);
    if (commonFunc.hasClassifiedQueryError(responseData)) {
      return commonFunc.resolveQueryErrorMessage(responseData, editorContext);
    }
    return null;
  },
  resolveQueryErrorMessage(responseData, editorContext = false) {
    if (!responseData) {
      return "";
    }
    if (responseData.databaseErrorTypeCode) {
      return commonFunc.resolveDbErrorMessage(
        responseData.databaseErrorTypeCode,
        responseData.userCode,
        editorContext,
      );
    }
    if (responseData.webServiceErrorTypeCode) {
      return commonFunc.resolveWebServiceErrorMessage(
        responseData.webServiceErrorTypeCode,
        responseData.userCode,
      );
    }
    return (
      responseData.errorListMessage ||
      responseData.errorMessage ||
      responseData.message ||
      ""
    );
  },
  formatString(sentence, ...args) {
    let formattedSentence = sentence;
    for (let i = 0; i < args.length; i++) {
      formattedSentence = formattedSentence.replaceAll(`{${i}}`, args[i]);
    }
    return formattedSentence;
  },
  // Encoding UTF8 ⇢ base64
  base64EncodeUnicode(str) {
    return btoa(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) =>
        String.fromCharCode(parseInt(p1, 16)),
      ),
    );
  },
  // Decoding base64 ⇢ UTF8
  base64DecodeUnicode(str) {
    return decodeURIComponent(
      Array.prototype.map
        .call(
          atob(str),
          (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2),
        )
        .join(""),
    );
  },
  decodeBase64ToUTF8(token) {
    const tokenPayload = token.split(".")[1];
    const decodedPayload = decodeURIComponent(escape(atob(tokenPayload)));
    const tokenInfoMap = JSON.parse(decodedPayload);
    return tokenInfoMap;
  },
  deepCopy(element) {
    if (element) {
      return JSON.parse(JSON.stringify(element));
    } else {
      return element;
    }
  },
  checkListAndJoin(array, delimeter) {
    if (!delimeter) {
      delimeter = ",";
    }
    return array && array.length > 0 ? array.join(delimeter) : "";
  },
  resolveHomepageQueryOrderKey(query) {
    if (!query) {
      return "";
    }
    const headerId = query.preparedQueryHeaderId;
    const hasHeaderId = headerId !== null && headerId !== undefined;
    if (query.type === "preparedQuery" && hasHeaderId) {
      return `preparedQuery:${headerId}`;
    }
    return query.type || "";
  },
  fetchIcon(icontype) {
    let serverAddress = window.location.origin;

    if (constant.serverAddress && constant.serverAddress != "/") {
      serverAddress = constant.serverAddress;
    }

    if (icontype && icontype.startsWith("/")) {
      return icontype;
    }

    if (icontype) {
      return `${serverAddress}${constant.staticFileUri}${icontype}`;
    } else {
      return `${serverAddress}${constant.nullIconValue}`;
    }
  },
  findEntityIdByName(entityName) {
    if (localStorage.entitiesWithAttributes) {
      const entitiesWithAttributes = this.getCompressedItem(
        "entitiesWithAttributes",
      );
      const foundEntity = entitiesWithAttributes.find(
        (item) => item.name == entityName,
      );
      if (foundEntity) {
        return foundEntity.id;
      }
      return -1;
    } else {
      this.errorHandler("metaDataLoadError");
    }
  },
  numberToTextTr(number) {
    const ones = [
      "",
      "bir",
      "iki",
      "üç",
      "dört",
      "beş",
      "altı",
      "yedi",
      "sekiz",
      "dokuz",
    ];
    const tens = [
      "",
      "on",
      "yirmi",
      "otuz",
      "kırk",
      "elli",
      "atmış",
      "yetmiş",
      "seksen",
      "doksan",
    ];
    const hundreds = [
      "",
      "yüz",
      "iki yüz",
      "üç yüz",
      "dört yüz",
      "beş yüz",
      "altı yüz",
      "yedi yüz",
      "sekiz yüz",
      "dokuz yüz",
    ];

    if (number === 0) {
      return "sıfır";
    }

    let text = "";

    if (number >= 100) {
      const hundredStep = Math.floor(number / 100);
      text += hundreds[hundredStep] + " ";
      number %= 100;
    }

    if (number >= 10) {
      const tenStep = Math.floor(number / 10);
      text += tens[tenStep];
      number %= 10;

      if (number > 0) {
        text += " " + ones[number];
      }
    } else if (number > 0) {
      text += ones[number];
    }

    return text;
  },
  copyText(text, textAreaId) {
    if (window.isSecureContext && navigator.clipboard) {
      navigator.clipboard.writeText(text);
    } else {
      this.unsecuredCopyToClipboard(text, textAreaId);
    }
  },
  unsecuredCopyToClipboard(text, textAreaId) {
    const textAreaElement = document.getElementById(textAreaId);
    textAreaElement.value = text;
    textAreaElement.focus();
    textAreaElement.select();
    document.execCommand("copy");
  },
  addLatestPhotoToItems(items, tabKey, latestPhotoMap) {
    return items.map((item) => {
      const photoKey = `${tabKey}_${item.id}`;
      const photoBase64 = latestPhotoMap[photoKey];
      const enriched = Object.assign({}, item);
      enriched.latestPhoto = photoBase64
        ? `data:image/jpg;base64,${photoBase64}`
        : null;
      return enriched;
    });
  },
  toImageDataUrl(value) {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    const raw = String(value);
    if (raw.startsWith("data:")) {
      return raw;
    }
    let mime = "image/png";
    if (raw.startsWith("/9j/")) {
      mime = "image/jpeg";
    } else if (raw.startsWith("R0lGOD")) {
      mime = "image/gif";
    }
    return `data:${mime};base64,${raw}`;
  },

  loadImageSizes(sources) {
    const unique = Array.from(new Set((sources || []).filter((source) => source)));
    if (unique.length === 0) {
      return Promise.resolve({});
    }
    return Promise.all(
      unique.map(
        (source) =>
          new Promise((resolve) => {
            const image = new Image();
            const timeoutId = setTimeout(() => resolve(null), IMAGE_SIZE_TIMEOUT);
            image.onload = () => {
              clearTimeout(timeoutId);
              resolve({ source: source, width: image.naturalWidth, height: image.naturalHeight });
            };
            image.onerror = () => {
              clearTimeout(timeoutId);
              resolve(null);
            };
            image.src = source;
          }),
      ),
    ).then((results) => {
      const sizeMap = {};
      results.forEach((result) => {
        if (result && result.width > 0 && result.height > 0) {
          sizeMap[result.source] = result;
        }
      });
      return sizeMap;
    });
  },
  async excelExport(data, worksheet, fileName, headerList, isShowTotalValue) {
    const filteredHeaderList = headerList.filter(
      (item) => item.datatype !== undefined,
    );
    const extension = ".xlsx";

    if (!fileName.includes(extension)) {
      fileName = `${fileName}${extension}`;
    }

    const headers = filteredHeaderList.map(({ value }) => value);
    // Toplam satiri da ayni diziden hesaplanir.
    const limitedData = await limitRows(data);
    const dataRows = limitedData.map((item) =>
      headers.map((header) => item[header]),
    );
    const workbook = new Exceljs.Workbook();
    const sheet = workbook.addWorksheet(`${worksheet}`);
    const imageColumns = [];
    filteredHeaderList.forEach((header, index) => {
      if (header.datatype === constant.ATTR_DATA_TYPES.Image) {
        imageColumns.push(index);
      }
    });
    const imageData = [];
    dataRows.forEach((row, rowIndex) => {
      imageColumns.forEach((colIndex) => {
        if (row[colIndex]) {
          imageData.push({
            rowIndex: rowIndex + 2, // +2 çünkü header var ve Excel 1'den başlar
            colIndex: colIndex,
            base64: row[colIndex],
          });
          row[colIndex] = ""; // Hücreyi boşalt
        }
      });
    });
    filteredHeaderList.forEach((header, index) => {
      if (header.datatype == constant.ATTR_DATA_TYPES.Currency) {
        dataRows.forEach((row) => {
          if (row[index]) {
            const rowIndexValue = row[index].toString();
            if (rowIndexValue.includes(",")) {
              const tempValue = parseFloat(rowIndexValue.replace(",", "."));
              if (tempValue != NaN) {
                row[index] = tempValue;
              }
            } else {
              row[index] = parseFloat(rowIndexValue);
            }
          }
        });
      } else if (header.datatype == constant.ATTR_DATA_TYPES.Numeric) {
        dataRows.forEach((row) => {
          if (row[index] != null) {
            row[index] = parseFloat(row[index]);
          }
        });
      } else if (
        header.datatype == constant.ATTR_DATA_TYPES.Datetime ||
        header.datatype == constant.ATTR_DATA_TYPES.Date
      ) {
        dataRows.forEach((row) => {
          const parsedDate = commonFunc.parseExcelDateString(row[index]);
          if (parsedDate) {
            row[index] = parsedDate;
          }
        });
      }
    });

    sheet.addTable({
      name: "Table",
      ref: "A1",
      headerColumns: true,
      style: {
        theme: "TableStyleLight1", // isteğe bağlı değiştirilebilir.  https://learn.microsoft.com/en-us/javascript/api/excel/excel.table?view=excel-js-preview
        showRowStripes: constant.SHOW_EXCEL_ROW_STRIPES,
      },
      columns: filteredHeaderList.map((header) => ({ name: header.text })),
      rows: dataRows,
    });

    filteredHeaderList.forEach((column, columnIndex) => {
      //Hücreler genişlik ayarı.. başlık vs data
      let maxCellWidth = column.text.length;
      sheet.eachRow({ includeEmpty: true }, (row, rowIndex) => {
        if (rowIndex > 1) {
          const cell = row.getCell(columnIndex + 1);
          const cellValue = cell.value;
          if (cellValue) {
            const cellWidth = cellValue.toString().length;
            if (cellWidth > maxCellWidth) {
              maxCellWidth = cellWidth;
            }
          }
        }
      });
      if (imageColumns.includes(columnIndex)) {
        sheet.columns[columnIndex].width = 10;
        sheet.getRow(1).height = 20;
        for (let i = 2; i <= dataRows.length + 1; i++) {
          sheet.getRow(i).height = 60;
        }
      } else {
        sheet.columns[columnIndex].width = Math.min(maxCellWidth + 3, 50);
      }
      if (
        filteredHeaderList[columnIndex] &&
        filteredHeaderList[columnIndex].datatype &&
        filteredHeaderList[columnIndex].datatype ===
        constant.ATTR_DATA_TYPES.Currency &&
        isShowTotalValue
      ) {
        const total = this.sumCurrencyData(
          filteredHeaderList[columnIndex],
          limitedData,
          true,
        );
        const lastRowIndex = sheet.columns[columnIndex].values.length;
        sheet.getCell(lastRowIndex, columnIndex + 1).value = total.includes(",")
          ? parseFloat(total.replace(",", "."))
          : parseFloat(total);
      }
      sheet.getColumn(sheet.columns[columnIndex].letter).alignment = {
        horizontal: "left",
      };

      if (
        filteredHeaderList[columnIndex] &&
        filteredHeaderList[columnIndex].datatype &&
        filteredHeaderList[columnIndex].datatype ==
        constant.ATTR_DATA_TYPES.Datetime
      ) {
        sheet.getColumn(sheet.columns[columnIndex].letter).numFmt =
          "dd.mm.yyyy hh:mm:ss";
      } else if (
        filteredHeaderList[columnIndex] &&
        filteredHeaderList[columnIndex].datatype &&
        filteredHeaderList[columnIndex].datatype ==
        constant.ATTR_DATA_TYPES.Date
      ) {
        sheet.getColumn(sheet.columns[columnIndex].letter).numFmt =
          "dd.mm.yyyy";
      } else if (
        filteredHeaderList[columnIndex] &&
        filteredHeaderList[columnIndex].datatype &&
        filteredHeaderList[columnIndex].datatype ==
        constant.ATTR_DATA_TYPES.Currency
      ) {
        sheet.getColumn(sheet.columns[columnIndex].letter).numFmt =
          "###,###.00"; //excel formatlaması bu şekilde excel indirilince 1.234.567,00 şeklinde gözüküyor
      } else if (
        filteredHeaderList[columnIndex] &&
        filteredHeaderList[columnIndex].datatype &&
        filteredHeaderList[columnIndex].datatype ===
        constant.ATTR_DATA_TYPES.Numeric
      ) {
        const columnLetter = sheet.columns[columnIndex].letter;
        for (let i = 2; i <= sheet.rowCount; i++) {
          const cell = sheet.getCell(`${columnLetter}${i}`);
          cell.alignment = { horizontal: "right" };
        }
      }
    });
    imageData.forEach((img) => {
      try {
        let base64Data = img.base64;
        if (base64Data.startsWith("data:image")) {
          base64Data = base64Data.split(",")[1];
        }
        const imageId = workbook.addImage({
          base64: base64Data,
          extension: "jpeg",
        });
        const columnLetter = sheet.getColumn(img.colIndex + 1).letter;
        sheet.addImage(imageId, {
          tl: { col: img.colIndex + 0.1, row: img.rowIndex - 1 + 0.1 },
          ext: { width: 60, height: 80 },
        });
      } catch (error) {
        console.error("Error while inserting image:", error);
      }
    });
    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        `${fileName}`,
      );
    });
  },
  async excelExportMultiSheet(sheetsData, fileName, isShowTotalValue) {
    const workbook = new Exceljs.Workbook();
    const usedSheetNames = {};

    for (const sheetInfo of sheetsData) {
      const filteredHeaderList = sheetInfo.headers.filter(
        (item) => item.datatype !== undefined,
      );
      if (filteredHeaderList.length === 0) {
        continue;
      }
      const headers = filteredHeaderList.map(({ value }) => value);
      const limitedItems = await limitRows(sheetInfo.items);
      const dataRows = limitedItems.map((item) =>
        headers.map((header) => item[header]),
      );
      // ExcelJS worksheet adında * ? : \ / [ ] karakterleri yasak, max 31 karakter ve tekil olmalı.
      let baseSheetName = (sheetInfo.sheetName || "Sheet")
        .replace(/[\\/?*[\]:]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .substring(0, 31);
      if (!baseSheetName) {
        baseSheetName = "Sheet";
      }
      let sheetName = baseSheetName;
      let duplicateCounter = 1;
      while (usedSheetNames[sheetName]) {
        const suffix = ` (${duplicateCounter})`;
        sheetName = `${baseSheetName.substring(
          0,
          31 - suffix.length,
        )}${suffix}`;
        duplicateCounter++;
      }
      usedSheetNames[sheetName] = true;
      const sheet = workbook.addWorksheet(sheetName);

      const imageColumns = [];
      filteredHeaderList.forEach((header, index) => {
        if (header.datatype === constant.ATTR_DATA_TYPES.Image) {
          imageColumns.push(index);
        }
      });

      const imageData = [];
      if (imageColumns.length > 0) {
        dataRows.forEach((row, rowIndex) => {
          imageColumns.forEach((colIndex) => {
            if (row[colIndex]) {
              imageData.push({
                rowIndex: rowIndex + 2,
                colIndex: colIndex,
                base64: row[colIndex],
              });
              row[colIndex] = "";
            }
          });
        });
      }

      filteredHeaderList.forEach((header, index) => {
        if (header.datatype == constant.ATTR_DATA_TYPES.Currency) {
          dataRows.forEach((row) => {
            if (row[index]) {
              const rowIndexValue = row[index].toString();
              if (rowIndexValue.includes(",")) {
                const tempValue = parseFloat(rowIndexValue.replace(",", "."));
                if (tempValue != NaN) {
                  row[index] = tempValue;
                }
              } else {
                row[index] = parseFloat(rowIndexValue);
              }
            }
          });
        } else if (header.datatype == constant.ATTR_DATA_TYPES.Numeric) {
          dataRows.forEach((row) => {
            if (row[index] != null) {
              row[index] = parseFloat(row[index]);
            }
          });
        } else if (
          header.datatype == constant.ATTR_DATA_TYPES.Datetime ||
          header.datatype == constant.ATTR_DATA_TYPES.Date
        ) {
          dataRows.forEach((row) => {
            const parsedDate = commonFunc.parseExcelDateString(row[index]);
            if (parsedDate) {
              row[index] = parsedDate;
            }
          });
        }
      });

      const tableName = `Table_${sheetInfo.sheetName || "Sheet"}`
        .replace(/[^a-zA-Z0-9_]/g, "_")
        .substring(0, 50);
      sheet.addTable({
        name: tableName,
        ref: "A1",
        headerColumns: true,
        style: {
          theme: "TableStyleLight1",
          showRowStripes: constant.SHOW_EXCEL_ROW_STRIPES,
        },
        columns: filteredHeaderList.map((header) => ({ name: header.text })),
        rows: dataRows,
      });

      filteredHeaderList.forEach((column, columnIndex) => {
        let maxCellWidth = column.text.length;
        sheet.eachRow({ includeEmpty: true }, (row, rowIndex) => {
          if (rowIndex > 1) {
            const cell = row.getCell(columnIndex + 1);
            const cellValue = cell.value;
            if (cellValue) {
              const cellWidth = cellValue.toString().length;
              if (cellWidth > maxCellWidth) {
                maxCellWidth = cellWidth;
              }
            }
          }
        });

        if (imageColumns.includes(columnIndex)) {
          sheet.columns[columnIndex].width = 10;
          sheet.getRow(1).height = 20;
          for (let i = 2; i <= dataRows.length + 1; i++) {
            sheet.getRow(i).height = 60;
          }
        } else {
          sheet.columns[columnIndex].width = Math.min(maxCellWidth + 3, 50);
        }

        if (
          filteredHeaderList[columnIndex] &&
          filteredHeaderList[columnIndex].datatype ===
          constant.ATTR_DATA_TYPES.Currency &&
          isShowTotalValue
        ) {
          const total = this.sumCurrencyData(
            filteredHeaderList[columnIndex],
            limitedItems,
            true,
          );
          const lastRowIndex = sheet.columns[columnIndex].values.length;
          sheet.getCell(lastRowIndex, columnIndex + 1).value = total.includes(
            ",",
          )
            ? parseFloat(total.replace(",", "."))
            : parseFloat(total);
        }

        sheet.getColumn(sheet.columns[columnIndex].letter).alignment = {
          horizontal: "left",
        };

        if (
          filteredHeaderList[columnIndex] &&
          filteredHeaderList[columnIndex].datatype ==
          constant.ATTR_DATA_TYPES.Datetime
        ) {
          sheet.getColumn(sheet.columns[columnIndex].letter).numFmt =
            "dd.mm.yyyy hh:mm:ss";
        } else if (
          filteredHeaderList[columnIndex] &&
          filteredHeaderList[columnIndex].datatype ==
          constant.ATTR_DATA_TYPES.Date
        ) {
          sheet.getColumn(sheet.columns[columnIndex].letter).numFmt =
            "dd.mm.yyyy";
        } else if (
          filteredHeaderList[columnIndex] &&
          filteredHeaderList[columnIndex].datatype ==
          constant.ATTR_DATA_TYPES.Currency
        ) {
          sheet.getColumn(sheet.columns[columnIndex].letter).numFmt =
            "###,###.00";
        } else if (
          filteredHeaderList[columnIndex] &&
          filteredHeaderList[columnIndex].datatype ===
          constant.ATTR_DATA_TYPES.Numeric
        ) {
          const columnLetter = sheet.columns[columnIndex].letter;
          for (let i = 2; i <= sheet.rowCount; i++) {
            const cell = sheet.getCell(`${columnLetter}${i}`);
            cell.alignment = { horizontal: "right" };
          }
        }
      });

      imageData.forEach((img) => {
        try {
          let base64Data = img.base64;
          if (base64Data.startsWith("data:image")) {
            base64Data = base64Data.split(",")[1];
          }
          const imageId = workbook.addImage({
            base64: base64Data,
            extension: "jpeg",
          });
          sheet.addImage(imageId, {
            tl: { col: img.colIndex + 0.1, row: img.rowIndex - 1 + 0.1 },
            ext: { width: 60, height: 80 },
          });
        } catch (error) {
          console.error("Error : ", error);
        }
      });
    }

    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        fileName,
      );
    });
  },
  currencyFormatter(data) {
    return parseFloat(data.toString().replace(",", ".")).toLocaleString(
      constant.CURRENCY_LOCALE_INFO[VueI18n.locale].currencyTitle,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        currency: constant.CURRENCY_LOCALE_INFO[VueI18n.locale].currencyType,
      },
    );
  },
  sumCurrencyData(element, items, isForExcel = false) {
    const calculatedItem = items
      .reduce((a, b) => {
        let value;
        if (b && b[element.value]) {
          value = parseFloat(b[element.value].toString().replace(",", "."));
        } else {
          value = 0;
        }
        return a + (value || 0);
      }, 0)
      .toString();
    if (isForExcel) {
      return calculatedItem;
    }
    return tableHelper.createTableContents(calculatedItem, element, 20);
  },
  normalizeSearchValue(value, search, item) {
    const normalizedSearch = search ? this.normalizeTurkish(search) : "";
    const normalizedValue = value ? this.normalizeTurkish(value) : "";
    return normalizedValue.includes(normalizedSearch);
  },
  normalizeString(str) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  },
  normalizeTurkish(text) {
    if (!text) return "";
    return text
      .toString()
      .replace(/İ/g, "i")
      .replace(/I/g, "i")
      .replace(/ı/g, "i")
      .replace(/Ş/g, "s")
      .replace(/Ğ/g, "g")
      .replace(/Ü/g, "u")
      .replace(/Ö/g, "o")
      .replace(/Ç/g, "c")
      .replace(/ş/g, "s")
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c")
      .toLowerCase();
  },
  getParameterStringValue(
    item,
    col,
    saveAsStringMap,
    parameterEntityMaps,
    entitiesWithAttributes,
  ) {
    if (saveAsStringMap && parameterEntityMaps && entitiesWithAttributes) {
      const columnNameParsed = col.columnName.split("_");
      if (
        entitiesWithAttributes &&
        columnNameParsed &&
        columnNameParsed.length == 3
      ) {
        const entity = entitiesWithAttributes.find(
          (t) => t.id == columnNameParsed[1],
        );
        const attr = entity.attributeList.find(
          (t) => t.id == columnNameParsed[2],
        );
        if (
          saveAsStringMap[entity.name] &&
          saveAsStringMap[entity.name][attr.name]
        ) {
          const parameterMap =
            parameterEntityMaps[
            saveAsStringMap[entity.name][attr.name].fillEntityId
            ];
          if (parameterMap) {
            const found = parameterMap.find(
              (t) =>
                t[
                saveAsStringMap[entity.name][attr.name]
                  .valueEntityAttributeName
                ] == item[col.value],
            );
            if (found) {
              return (
                found[
                saveAsStringMap[entity.name][attr.name]
                  .textEntityAttributeName
                ] ?? item[col.value]
              );
            }
          }
        }
      }
      return item[col.value];
    }
    return item[col.value];
  },
  getCurrentDateAndTime() {
    const now = new Date();
    const formattedDate = now
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "");
    const formattedTime = now
      .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      .replace(/:/g, "");
    return `${formattedDate}_${formattedTime}`;
  },
  getCurrentDate() {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();
    return `${day}.${month}.${year}`;
  },
  pastedOnTextField(e, newText) {
    const pastedArr = e.clipboardData.getData("text/plain").split("\n");
    const lastChar = newText.slice(-1);
    if (newText && lastChar != ",") {
      newText += ",";
    }
    pastedArr.forEach((arr, i) => {
      if (arr != "") {
        if (arr[arr.length - 1] == "\r") {
          newText += arr.substring(0, arr.length - 1);
        } else if (arr[0] == "\r") {
          newText += arr.substring(1, arr.length);
        } else {
          newText += arr.substring(0, arr.length);
        }
        if (i != pastedArr.length - 1) newText += ",";
      }
    });
    e.preventDefault();
    return newText;
  },
  setInfoCardTheme(infoCard) {
    let selectedTheme = "";
    let isBlackText = null;
    if (infoCard.selectedTheme && infoCard.selectedTheme.includes(",")) {
      const theme = infoCard.selectedTheme.split(",");
      selectedTheme = theme[0];
      isBlackText = theme[1] == "true";
    } else {
      selectedTheme = infoCard.selectedTheme;
    }
    const theme = {
      selectedTheme: selectedTheme,
      isBlackText: isBlackText,
    };
    return theme;
  },
  clearSessionStorage() {
    sessionStorage.removeItem("analysisId");
    sessionStorage.removeItem("analysisQueryId");
    sessionStorage.removeItem("analysisReason");
  },
  setCompressedItem(key, value) {
    try {
      const jsonString =
        typeof value === "string" ? value : JSON.stringify(value);
      const compressed = LZString.compressToBase64(jsonString);
      localStorage.setItem(key, compressed);
    } catch (error) {
      console.error("Compression error for key:", key, error);
      localStorage.setItem(
        key,
        typeof value === "string" ? value : JSON.stringify(value),
      );
    }
  },
  getCompressedItem(key) {
    try {
      const compressed = localStorage.getItem(key);
      if (!compressed) {
        return null;
      }
      const decompressed = LZString.decompressFromBase64(compressed);
      if (decompressed === null) {
        return localStorage.getItem(key);
      }
      try {
        return JSON.parse(decompressed);
      } catch {
        return decompressed;
      }
    } catch (error) {
      console.error("Decompression error for key:", key, error);
      return localStorage.getItem(key);
    }
  },
  getCustomerEntityName() {
    let customerEntityName;
    const clientSettings = localStorage.clientSettings
      ? JSON.parse(localStorage.clientSettings)
      : {};

    if (
      clientSettings.CustomerEntityName &&
      clientSettings.CustomerEntityName.trim() !== ""
    ) {
      customerEntityName = clientSettings.CustomerEntityName;
    } else {
      customerEntityName = constant.CUSTOMER_ENTITY_NAME;
    }
    return customerEntityName;
  },
  isKycV2Enabled() {
    const clientSettings = localStorage.clientSettings
      ? JSON.parse(localStorage.clientSettings)
      : {};
    return (
      String(clientSettings.KycV2Enabled).toLowerCase() === "true" &&
      String(clientSettings.KycV2Compiled).toLowerCase() !== "false"
    );
  },
  isKycV1Enabled() {
    const clientSettings = localStorage.clientSettings
      ? JSON.parse(localStorage.clientSettings)
      : {};
    return String(clientSettings.KycV1Compiled).toLowerCase() !== "false";
  },
  isDynamicOriginModule(originModule) {
    return (
      String(originModule).toLowerCase() === constant.KYC_ORIGIN_MODULE.DYNAMIC
    );
  },
  getPepAndSanctionMapValue(keyprm) {
    //filtre değerine sahip en az bir key olması beklenir.
    //keyperm  role.pep ise clientsetting'teki pepAndSanctionMappings'i kullanarak şu işlem yapılır:
    //role.pep keyinin değeri alınır: PEP  daha sonra aynı değere sahip diğer key'ler bulunarak joinlenir:
    //"role.pep,ROLE.PEP"

    const clientSettings = localStorage.clientSettings
      ? JSON.parse(localStorage.clientSettings)
      : {};

    const mappingsRaw = clientSettings.pepAndSanctionMappings;
    if (!mappingsRaw) {
      return keyprm;
    }

    const mappings = JSON.parse(mappingsRaw);

    const directValue = mappings[keyprm];
    if (!directValue) {
      return keyprm;
    }

    const matchingKeys = Object.keys(mappings).filter(
      (k) => mappings[k] === directValue,
    );

    return matchingKeys.join(",");
  },
  formatCurrency(value) {
    if (value) {
      if (value.includes(",")) {
        value = parseFloat(value.replace(",", "."));
      } else {
        value = parseFloat(value);
      }
      return value.toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else {
      return "";
    }
  },
  sanitizeHtml(html) {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "b",
        "i",
        "u",
        "strong",
        "em",
        "p",
        "br",
        "ul",
        "ol",
        "li",
        "span",
        "div",
        "code",
        "pre",
        "blockquote",
      ],
      ALLOWED_ATTR: ["style"],
      ALLOWED_CSS_PROPERTIES: [
        "color",
        "background-color",
        "font-weight",
        "text-decoration",
        "font-style",
      ],
    });
  },
  // Bildirim içerikleri backend'den HTML formatlanmış gelebilir; tablo ve başlık
  // etiketleri de desteklendiği için ayrı bir allowlist kullanılır.
  sanitizeNotificationHtml(html) {
    return DOMPurify.sanitize(`${html == null ? "" : html}`, {
      ALLOWED_TAGS: [
        "b",
        "i",
        "u",
        "strong",
        "em",
        "small",
        "p",
        "br",
        "hr",
        "span",
        "div",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "table",
        "thead",
        "tbody",
        "tfoot",
        "tr",
        "th",
        "td",
        "code",
        "pre",
        "blockquote",
      ],
      ALLOWED_ATTR: ["style", "class", "colspan", "rowspan", "align"],
    });
  },
  isExcelFile(file) {
    if (!file || !file.name) {
      return false;
    }

    const validExtensions = [".xls", ".xlsx", ".csv"];
    const fileName = file.name.toLowerCase();

    return validExtensions.some((extension) => fileName.endsWith(extension));
  },
  getWritableEntityItemText(item) {
    if (!item) {
      return "";
    }
    if (item.entityAlias) {
      return String(item.entityAlias);
    }
    return String(item.name);
  },

  // Rapor stili Excel export: üst başlık satırı + opsiyonel grup başlık satırı + sütun başlıkları + veri
  // config: { title, sheetName, fileName, columnGroups, columns, rows }
  //   columnGroups: [{ label, startKey, endKey }]
  //   columns: [{ key, header, width, align, format, emptyValue, parentHeader }]
  //     format: "integer" | "decimal" | undefined
  //     parentHeader: ardışık aynı parentHeader'a sahip sütunlar bir üst başlık satırında birleştirilir;
  //                   parentHeader'ı olmayan sütunların başlığı bu satırla dikey olarak birleşir
  // NOT: title, sheetName, column.header, columnGroup.label gibi tüm metinler
  //      çağıran tarafından i18n ile localize edilmiş olarak gelmelidir.
  async exportReportToExcel(config) {
    const cfg = config || {};
    const columns = cfg.columns || [];
    const rows = await limitRows(cfg.rows || []);
    const columnGroups = cfg.columnGroups || [];
    const title = cfg.title || "";
    const sheetName = (cfg.sheetName || "Rapor").substring(0, 31);
    const fileName = cfg.fileName || "rapor.xlsx";

    const isLandscape = cfg.orientation === "landscape";
    const workbook = new Exceljs.Workbook();
    const sheet = workbook.addWorksheet(sheetName, {
      pageSetup: {
        paperSize: 9, // A4
        orientation: isLandscape ? "landscape" : "portrait",
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        horizontalCentered: true,
        margins: {
          left: 0.3,
          right: 0.3,
          top: 0.4,
          bottom: 0.5,
          header: 0.2,
          footer: 0.3,
        },
      },
    });
    const colCount = columns.length;

    const totalWeight = columns.reduce((sum, c) => sum + (c.width || 1), 0);
    const targetCharWidth = isLandscape ? 145 : 100;
    const computedColWidths = columns.map((col) => {
      const weight = col.width || 1;
      const charWidth = (weight / totalWeight) * targetCharWidth;
      return Math.max(6, Math.min(60, charWidth));
    });
    columns.forEach((col, idx) => {
      sheet.getColumn(idx + 1).width = computedColWidths[idx];
    });

    const estimateWrapLines = (text, charWidth) => {
      if (!text || charWidth < 1) {
        return 1;
      }
      // Excel char width unit ≈ width of "0" in default font; mixed-case
      // text is wider than monospace assumption. Use ~85% effective width
      // to err on the side of slightly more lines (better too tall than clipped).
      const effectiveWidth = Math.max(1, charWidth * 0.85);
      const words = String(text)
        .split(/\s+/)
        .filter((w) => w.length > 0);
      if (words.length === 0) {
        return 1;
      }
      let lines = 1;
      let lineFill = 0;
      words.forEach((word) => {
        if (lineFill === 0) {
          lineFill = word.length;
        } else if (lineFill + 1 + word.length <= effectiveWidth) {
          lineFill += 1 + word.length;
        } else {
          lines++;
          lineFill = word.length;
        }
        if (word.length > effectiveWidth) {
          lines += Math.floor(word.length / effectiveWidth);
        }
      });
      return lines;
    };

    let currentRowIndex = 1;

    if (title) {
      sheet.mergeCells(currentRowIndex, 1, currentRowIndex, colCount);
      const titleCell = sheet.getCell(currentRowIndex, 1);
      titleCell.value = title;
      titleCell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      titleCell.font = { bold: true, size: 14 };
      titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFEFF6FF" },
      };
      const totalSheetWidth = computedColWidths.reduce((sum, w) => sum + w, 0);
      const titleLines = estimateWrapLines(title, totalSheetWidth);
      sheet.getRow(currentRowIndex).height = Math.max(26, titleLines * 18 + 6);
      currentRowIndex++;
    }

    const hasGroups = columnGroups.length > 0;
    if (hasGroups) {
      const groupRowIndex = currentRowIndex;
      const keyToIndex = {};
      columns.forEach((col, idx) => {
        keyToIndex[col.key] = idx + 1;
      });
      columns.forEach((col, idx) => {
        const cell = sheet.getCell(groupRowIndex, idx + 1);
        cell.value = "";
        this._applyReportHeaderStyle(cell);
      });
      let maxGroupLines = 1;
      columnGroups.forEach((group) => {
        const startIdx = keyToIndex[group.startKey];
        const endIdx = keyToIndex[group.endKey];
        if (!startIdx || !endIdx) {
          return;
        }
        if (startIdx === endIdx) {
          const cell = sheet.getCell(groupRowIndex, startIdx);
          cell.value = group.label;
        } else {
          sheet.mergeCells(groupRowIndex, startIdx, groupRowIndex, endIdx);
          const cell = sheet.getCell(groupRowIndex, startIdx);
          cell.value = group.label;
        }
        let groupTotalWidth = 0;
        for (let i = startIdx; i <= endIdx; i++) {
          groupTotalWidth += computedColWidths[i - 1];
        }
        const lines = estimateWrapLines(group.label, groupTotalWidth);
        if (lines > maxGroupLines) {
          maxGroupLines = lines;
        }
      });
      sheet.getRow(groupRowIndex).height = Math.max(24, maxGroupLines * 16 + 6);
      currentRowIndex++;
    }

    const hasParentHeaders = columns.some((c) => c.parentHeader);
    let parentHeaderRowIndex = null;
    if (hasParentHeaders) {
      parentHeaderRowIndex = currentRowIndex;
      columns.forEach((col, idx) => {
        const cell = sheet.getCell(parentHeaderRowIndex, idx + 1);
        cell.value = "";
        this._applyReportHeaderStyle(cell);
      });
      let i = 0;
      let maxParentLines = 1;
      while (i < columns.length) {
        const col = columns[i];
        if (col.parentHeader) {
          let j = i;
          while (
            j + 1 < columns.length &&
            columns[j + 1].parentHeader === col.parentHeader
          ) {
            j++;
          }
          if (j > i) {
            sheet.mergeCells(
              parentHeaderRowIndex,
              i + 1,
              parentHeaderRowIndex,
              j + 1,
            );
          }
          const cell = sheet.getCell(parentHeaderRowIndex, i + 1);
          cell.value = col.parentHeader;
          this._applyReportHeaderStyle(cell);
          let runWidth = 0;
          for (let k = i; k <= j; k++) {
            runWidth += computedColWidths[k];
          }
          const lines = estimateWrapLines(col.parentHeader, runWidth);
          if (lines > maxParentLines) {
            maxParentLines = lines;
          }
          i = j + 1;
        } else {
          i++;
        }
      }
      sheet.getRow(parentHeaderRowIndex).height = Math.max(
        22,
        maxParentLines * 15 + 6,
      );
      currentRowIndex++;
    }

    const headerRowIndex = currentRowIndex;
    let maxHeaderLines = 1;
    columns.forEach((col, idx) => {
      const cell = sheet.getCell(headerRowIndex, idx + 1);
      if (hasParentHeaders && !col.parentHeader) {
        cell.value = "";
        this._applyReportHeaderStyle(cell);
      } else {
        cell.value = col.header || col.key;
        this._applyReportHeaderStyle(cell);
        if (hasParentHeaders && col.parentHeader) {
          cell.font = { bold: true, size: 8, color: { argb: "FF1E293B" } };
        }
        const lines = estimateWrapLines(
          col.header || col.key,
          computedColWidths[idx],
        );
        if (lines > maxHeaderLines) {
          maxHeaderLines = lines;
        }
      }
    });
    if (hasParentHeaders) {
      sheet.getRow(headerRowIndex).height = Math.max(
        15,
        maxHeaderLines * 11 + 3,
      );
    } else {
      sheet.getRow(headerRowIndex).height = Math.max(
        28,
        maxHeaderLines * 15 + 6,
      );
    }
    currentRowIndex++;

    if (hasParentHeaders) {
      columns.forEach((col, idx) => {
        if (!col.parentHeader) {
          const topCell = sheet.getCell(parentHeaderRowIndex, idx + 1);
          topCell.value = col.header || col.key;
          this._applyReportHeaderStyle(topCell);
          sheet.mergeCells(
            parentHeaderRowIndex,
            idx + 1,
            headerRowIndex,
            idx + 1,
          );
        }
      });
    }

    rows.forEach((row) => {
      columns.forEach((col, idx) => {
        const cell = sheet.getCell(currentRowIndex, idx + 1);
        const rawValue = row[col.key];
        const isEmpty =
          rawValue === null || rawValue === undefined || rawValue === "";
        if (isEmpty) {
          cell.value = col.emptyValue !== undefined ? col.emptyValue : "";
        } else if (col.format === "integer") {
          cell.value = Number(rawValue);
          cell.numFmt = "#,##0";
        } else if (col.format === "decimal") {
          cell.value = Number(rawValue);
          cell.numFmt = "#,##0.00";
        } else {
          cell.value = rawValue;
        }
        cell.alignment = {
          horizontal: col.align || "left",
          vertical: "middle",
          wrapText: true,
        };
        cell.font = { size: 9 };
        cell.border = {
          top: { style: "thin", color: { argb: "FFD0D7E2" } },
          left: { style: "thin", color: { argb: "FFD0D7E2" } },
          bottom: { style: "thin", color: { argb: "FFD0D7E2" } },
          right: { style: "thin", color: { argb: "FFD0D7E2" } },
        };
      });
      currentRowIndex++;
    });

    const freezeRow = headerRowIndex;
    sheet.views = [{ state: "frozen", ySplit: freezeRow }];
    sheet.pageSetup.printTitlesRow = `1:${headerRowIndex}`;
    sheet.pageSetup.printArea = `A1:${sheet.getColumn(colCount).letter
      }${currentRowIndex - 1}`;

    const finalFileName = fileName.toLowerCase().endsWith(".xlsx")
      ? fileName
      : `${fileName}.xlsx`;
    return workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(
        new Blob([buffer], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        finalFileName,
      );
    });
  },

  _applyReportHeaderStyle(cell) {
    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    cell.font = { bold: true, size: 10, color: { argb: "FF1E293B" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE2E8F0" },
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FF94A3B8" } },
      left: { style: "thin", color: { argb: "FF94A3B8" } },
      bottom: { style: "thin", color: { argb: "FF94A3B8" } },
      right: { style: "thin", color: { argb: "FF94A3B8" } },
    };
  },

  // Rapor stili PDF export — jsPDF ile elle çizim (autotable yok)
  // config: { title, fileName, orientation, pageSize, columnGroups, columns, rows, pageLabel, watermark }
  //   columns ve columnGroups Excel ile aynı şema
  //   columns[i].width: göreceli ağırlık (sayfa genişliğine oranlanır)
  //   pageLabel: (current, total) => string — sayfa altındaki etiket (opsiyonel)
  //   watermark: her sayfaya çizilecek filigran metni (opsiyonel)
  //   colors: { groupFill, columnHeaderFill, rowStripe, rowBorder } — RGB dizileri, varsayılanı ezer (opsiyonel)
  //   columns[i].isImage: hücre base64/data URL resim olarak çizilir, metin yazılmaz (opsiyonel)
  // NOT: title, column.header, columnGroup.label gibi tüm metinler çağıran
  //      tarafından i18n ile localize edilmiş olarak gelmelidir. pageLabel
  //      callback'i de localize edilmiş string döndürmelidir.
  exportReportToPdf(config) {
    const cfg = config || {};
    const orientation = cfg.orientation || "landscape";
    const pageSize = cfg.pageSize || "a4";
    const fileName = cfg.fileName || "rapor.pdf";
    const title = cfg.title || "";
    const columns = cfg.columns || [];
    const rows = cfg.rows || [];
    const columnGroups = cfg.columnGroups || [];
    const finalFileName = fileName.toLowerCase().endsWith(".pdf")
      ? fileName
      : `${fileName}.pdf`;

    const imageColumns = columns.filter((col) => col.isImage);
    const imageSources = new Set();
    rows.forEach((row) => {
      imageColumns.forEach((col) => {
        const source = commonFunc.toImageDataUrl(row[col.key]);
        if (source) {
          imageSources.add(source);
        }
      });
    });

    return Promise.all([
      import("jspdf"),
      import("@/assets/fonts/Roboto-Regular-base64.js"),
      import("@/assets/fonts/Roboto-Bold-base64.js"),
      commonFunc.loadImageSizes(Array.from(imageSources)),
    ]).then(([jspdfModule, regularModule, boldModule, imageSizeMap]) => {
      const JsPDF = jspdfModule.jsPDF;
      const RobotoRegularBase64 = regularModule.default;
      const RobotoBoldBase64 = boldModule.default;
      const doc = new JsPDF({
        orientation: orientation,
        unit: "mm",
        format: pageSize,
      });

      const FONT_NAME = "Roboto";
      doc.addFileToVFS("Roboto-Regular.ttf", RobotoRegularBase64);
      doc.addFileToVFS("Roboto-Bold.ttf", RobotoBoldBase64);
      doc.addFont("Roboto-Regular.ttf", FONT_NAME, "normal");
      doc.addFont("Roboto-Bold.ttf", FONT_NAME, "bold");
      doc.setFont(FONT_NAME, "normal");

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginX = 8;
      const marginTop = 10;
      const marginBottom = 12;
      const usableWidth = pageWidth - marginX * 2;

      const totalWeight = columns.reduce((sum, c) => sum + (c.width || 1), 0);
      const colWidths = columns.map(
        (c) => ((c.width || 1) / totalWeight) * usableWidth,
      );
      const colOffsets = [];
      let offset = marginX;
      colWidths.forEach((w) => {
        colOffsets.push(offset);
        offset += w;
      });

      const hasGroups = columnGroups.length > 0;
      const titleHeight = title ? 7.5 : 0;
      const fontSize = 6.5;
      const lineHeight = 2.9;
      const titleFontSize = 11;
      const pageLabelFontSize = 8;

      const subHeaderFontSize = 5.3;
      const subHeaderLineHeight = 2.3;
      const MAX_IMAGE_HEIGHT = 18;
      const IMAGE_CELL_PADDING = 1;

      const computeColumnHeaderHeight = () => {
        const anyParent = columns.some((c) => c.parentHeader);
        let maxLines = 1;
        doc.setFont(FONT_NAME, "bold");
        columns.forEach((col, idx) => {
          if (anyParent && !col.parentHeader) {
            return;
          }
          const useSmaller = anyParent && col.parentHeader;
          doc.setFontSize(useSmaller ? subHeaderFontSize : fontSize);
          const text = col.header || col.key;
          const lines = doc.splitTextToSize(text, colWidths[idx] - 1.5);
          if (lines.length > maxLines) {
            maxLines = lines.length;
          }
        });
        if (anyParent) {
          return Math.max(3.8, maxLines * subHeaderLineHeight + 0.6);
        }
        return Math.max(6, maxLines * lineHeight + 2);
      };

      const computeGroupHeaderHeight = () => {
        if (!hasGroups) {
          return 0;
        }
        let maxLines = 1;
        doc.setFont(FONT_NAME, "bold");
        doc.setFontSize(fontSize);
        const keyToIndex = {};
        columns.forEach((col, idx) => {
          keyToIndex[col.key] = idx;
        });
        columnGroups.forEach((group) => {
          const startIdx = keyToIndex[group.startKey];
          const endIdx = keyToIndex[group.endKey];
          if (startIdx === undefined || endIdx === undefined) {
            return;
          }
          const x = colOffsets[startIdx];
          const w = colOffsets[endIdx] + colWidths[endIdx] - x;
          const lines = doc.splitTextToSize(group.label, w - 3);
          if (lines.length > maxLines) {
            maxLines = lines.length;
          }
        });
        return Math.max(6, maxLines * lineHeight + 2);
      };

      const hasParentHeaders = columns.some((c) => c.parentHeader);

      const computeParentHeaderHeight = () => {
        if (!hasParentHeaders) {
          return 0;
        }
        let maxLines = 1;
        doc.setFont(FONT_NAME, "bold");
        doc.setFontSize(fontSize);
        let i = 0;
        while (i < columns.length) {
          const col = columns[i];
          if (col.parentHeader) {
            let j = i;
            while (
              j + 1 < columns.length &&
              columns[j + 1].parentHeader === col.parentHeader
            ) {
              j++;
            }
            const x = colOffsets[i];
            const w = colOffsets[j] + colWidths[j] - x;
            const lines = doc.splitTextToSize(col.parentHeader, w - 1.5);
            if (lines.length > maxLines) {
              maxLines = lines.length;
            }
            i = j + 1;
          } else {
            i++;
          }
        }
        return Math.max(6, maxLines * lineHeight + 2);
      };

      const columnHeaderHeight = computeColumnHeaderHeight();
      const groupHeaderHeight = computeGroupHeaderHeight();
      const parentHeaderHeight = computeParentHeaderHeight();

      const headerBlockY = () => marginTop + titleHeight;
      const dataStartY = () =>
        headerBlockY() +
        groupHeaderHeight +
        parentHeaderHeight +
        columnHeaderHeight;

      const palette = cfg.colors || {};
      const BORDER_RGB = [148, 163, 184];
      const TEXT_DARK_RGB = [15, 23, 42];
      const TEXT_MUTED_RGB = [100, 116, 139];
      const TITLE_FILL_RGB = palette.titleFill || [239, 246, 255];
      const GROUP_FILL_RGB = palette.groupFill || [226, 232, 240];
      const COL_HEADER_FILL_RGB = palette.columnHeaderFill || [241, 245, 249];
      const ROW_STRIPE_RGB = palette.rowStripe || [248, 250, 252];
      const ROW_BORDER_RGB = palette.rowBorder || [208, 215, 226];

      const drawTitle = () => {
        if (!title) {
          return;
        }
        doc.setFillColor.apply(doc, TITLE_FILL_RGB);
        doc.rect(marginX, marginTop, usableWidth, titleHeight, "F");
        doc.setFont(FONT_NAME, "bold");
        doc.setFontSize(titleFontSize);
        doc.setTextColor.apply(doc, TEXT_DARK_RGB);
        doc.text(title, pageWidth / 2, marginTop + titleHeight - 2, {
          align: "center",
        });
      };

      const drawHeaderRow = () => {
        doc.setFont(FONT_NAME, "bold");
        doc.setFontSize(fontSize);
        doc.setDrawColor.apply(doc, BORDER_RGB);
        doc.setLineWidth(0.15);

        if (hasGroups) {
          const groupY = headerBlockY();
          const keyToIndex = {};
          columns.forEach((col, idx) => {
            keyToIndex[col.key] = idx;
          });
          const usedRange = new Array(columns.length).fill(false);
          columnGroups.forEach((group) => {
            const startIdx = keyToIndex[group.startKey];
            const endIdx = keyToIndex[group.endKey];
            if (startIdx === undefined || endIdx === undefined) {
              return;
            }
            const x = colOffsets[startIdx];
            const w = colOffsets[endIdx] + colWidths[endIdx] - x;
            doc.setFillColor.apply(doc, GROUP_FILL_RGB);
            doc.rect(x, groupY, w, groupHeaderHeight, "FD");
            doc.setTextColor.apply(doc, TEXT_DARK_RGB);
            const lines = doc.splitTextToSize(group.label, w - 3);
            const startTextY =
              groupY +
              (groupHeaderHeight - lines.length * lineHeight) / 2 +
              lineHeight -
              0.5;
            lines.forEach((line, lineIdx) => {
              doc.text(line, x + w / 2, startTextY + lineIdx * lineHeight, {
                align: "center",
              });
            });
            for (let i = startIdx; i <= endIdx; i++) {
              usedRange[i] = true;
            }
          });
          columns.forEach((col, idx) => {
            if (!usedRange[idx]) {
              doc.setFillColor.apply(doc, GROUP_FILL_RGB);
              doc.rect(
                colOffsets[idx],
                groupY,
                colWidths[idx],
                groupHeaderHeight,
                "FD",
              );
            }
          });
        }

        if (hasParentHeaders) {
          const parentY = headerBlockY() + groupHeaderHeight;
          let i = 0;
          while (i < columns.length) {
            const col = columns[i];
            if (col.parentHeader) {
              let j = i;
              while (
                j + 1 < columns.length &&
                columns[j + 1].parentHeader === col.parentHeader
              ) {
                j++;
              }
              const x = colOffsets[i];
              const w = colOffsets[j] + colWidths[j] - x;
              doc.setFillColor.apply(doc, COL_HEADER_FILL_RGB);
              doc.rect(x, parentY, w, parentHeaderHeight, "FD");
              doc.setTextColor.apply(doc, TEXT_DARK_RGB);
              const lines = doc.splitTextToSize(col.parentHeader, w - 1.5);
              const startTextY =
                parentY +
                (parentHeaderHeight - lines.length * lineHeight) / 2 +
                lineHeight -
                0.5;
              lines.forEach((line, lineIdx) => {
                doc.text(line, x + w / 2, startTextY + lineIdx * lineHeight, {
                  align: "center",
                });
              });
              i = j + 1;
            } else {
              i++;
            }
          }
        }

        const colHeaderY =
          headerBlockY() + groupHeaderHeight + parentHeaderHeight;
        columns.forEach((col, idx) => {
          const hasParent = hasParentHeaders && col.parentHeader;
          const noParentSpan = hasParentHeaders && !col.parentHeader;
          const cellY = noParentSpan
            ? headerBlockY() + groupHeaderHeight
            : colHeaderY;
          const cellHeight = noParentSpan
            ? parentHeaderHeight + columnHeaderHeight
            : columnHeaderHeight;
          doc.setFillColor.apply(doc, COL_HEADER_FILL_RGB);
          doc.rect(colOffsets[idx], cellY, colWidths[idx], cellHeight, "FD");
          doc.setTextColor.apply(doc, TEXT_DARK_RGB);
          const cellFontSize = hasParent ? subHeaderFontSize : fontSize;
          const cellLineHeight = hasParent ? subHeaderLineHeight : lineHeight;
          doc.setFontSize(cellFontSize);
          const text = col.header || col.key;
          const lines = doc.splitTextToSize(text, colWidths[idx] - 1.5);
          const startTextY =
            cellY +
            (cellHeight - lines.length * cellLineHeight) / 2 +
            cellLineHeight -
            0.5;
          lines.forEach((line, lineIdx) => {
            doc.text(
              line,
              colOffsets[idx] + colWidths[idx] / 2,
              startTextY + lineIdx * cellLineHeight,
              { align: "center" },
            );
          });
        });
        doc.setFontSize(fontSize);

        doc.setFont(FONT_NAME, "normal");
        doc.setTextColor.apply(doc, TEXT_DARK_RGB);
      };

      const formatCellValue = (col, rawValue) => {
        const isEmpty =
          rawValue === null || rawValue === undefined || rawValue === "";
        if (isEmpty) {
          return col.emptyValue !== undefined ? col.emptyValue : "";
        }
        if (col.format === "integer") {
          return Number(rawValue).toLocaleString("tr-TR", {
            maximumFractionDigits: 0,
          });
        }
        if (col.format === "decimal") {
          return Number(rawValue).toLocaleString("tr-TR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
        return String(rawValue);
      };

      // Resim hucresi: en boy orani korunarak sutun genisligine ve ust sinira sigdirilir.
      const resolveCellImage = (col, idx, row) => {
        if (!col.isImage) {
          return null;
        }
        const source = commonFunc.toImageDataUrl(row[col.key]);
        const size = source ? imageSizeMap[source] : null;
        if (!size) {
          return null;
        }
        const maxWidth = Math.max(1, colWidths[idx] - IMAGE_CELL_PADDING * 2);
        const ratio = size.height / size.width;
        const width = Math.min(maxWidth, MAX_IMAGE_HEIGHT / ratio);
        return { source: source, width: width, height: width * ratio };
      };

      const measureRow = (row) => {
        doc.setFont(FONT_NAME, "normal");
        doc.setFontSize(fontSize);
        let maxLines = 1;
        let maxImageHeight = 0;
        const cellImages = columns.map((col, idx) => {
          const image = resolveCellImage(col, idx, row);
          if (image && image.height > maxImageHeight) {
            maxImageHeight = image.height;
          }
          return image;
        });
        const cellLines = columns.map((col, idx) => {
          if (col.isImage) {
            return [];
          }
          const text = formatCellValue(col, row[col.key]);
          const lines = doc.splitTextToSize(
            String(text || ""),
            colWidths[idx] - 1.5,
          );
          if (lines.length > maxLines) {
            maxLines = lines.length;
          }
          return lines;
        });
        return {
          cellLines: cellLines,
          cellImages: cellImages,
          height: Math.max(
            5,
            maxLines * lineHeight + 1.5,
            maxImageHeight + IMAGE_CELL_PADDING * 2,
          ),
        };
      };

      const drawDataRow = (measured, y, rowIndex) => {
        doc.setFont(FONT_NAME, "normal");
        doc.setFontSize(fontSize);
        doc.setTextColor.apply(doc, TEXT_DARK_RGB);
        doc.setDrawColor.apply(doc, ROW_BORDER_RGB);
        doc.setLineWidth(0.1);
        const rowHeight = measured.height;
        const isStripe = rowIndex % 2 === 1;
        columns.forEach((col, idx) => {
          if (isStripe) {
            doc.setFillColor.apply(doc, ROW_STRIPE_RGB);
            doc.rect(colOffsets[idx], y, colWidths[idx], rowHeight, "FD");
          } else {
            doc.rect(colOffsets[idx], y, colWidths[idx], rowHeight, "D");
          }
          if (col.isImage) {
            const image = measured.cellImages[idx];
            if (image) {
              // Desteklenmeyen bir resim tum raporu dusurmesin diye hucre bos birakilir.
              try {
                doc.addImage(
                  image.source,
                  colOffsets[idx] + (colWidths[idx] - image.width) / 2,
                  y + (rowHeight - image.height) / 2,
                  image.width,
                  image.height,
                );
              } catch (error) {
                console.error("PDF resim hatası:", error);
              }
            }
            return;
          }
          const lines = measured.cellLines[idx];
          const align = col.align || "left";
          let textX;
          let alignOption;
          if (align === "center") {
            textX = colOffsets[idx] + colWidths[idx] / 2;
            alignOption = "center";
          } else if (align === "right") {
            textX = colOffsets[idx] + colWidths[idx] - 1.2;
            alignOption = "right";
          } else {
            textX = colOffsets[idx] + 1.2;
            alignOption = "left";
          }
          const startTextY =
            y + (rowHeight - lines.length * lineHeight) / 2 + lineHeight - 0.5;
          lines.forEach((line, lineIdx) => {
            doc.text(line, textX, startTextY + lineIdx * lineHeight, {
              align: alignOption,
            });
          });
        });
        return rowHeight;
      };

      drawTitle();
      drawHeaderRow();
      let currentY = dataStartY();
      let rowCounter = 0;

      rows.forEach((row) => {
        const measured = measureRow(row);
        if (currentY + measured.height > pageHeight - marginBottom) {
          doc.addPage();
          drawTitle();
          drawHeaderRow();
          currentY = dataStartY();
          rowCounter = 0;
        }
        drawDataRow(measured, currentY, rowCounter);
        currentY += measured.height;
        rowCounter++;
      });

      const pageCount = doc.internal.getNumberOfPages();
      const pageLabelFn =
        typeof cfg.pageLabel === "function"
          ? cfg.pageLabel
          : (current, total) => `${current} / ${total}`;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFont(FONT_NAME, "normal");
        doc.setFontSize(pageLabelFontSize);
        doc.setTextColor.apply(doc, TEXT_MUTED_RGB);
        const pageLabel = pageLabelFn(i, pageCount);
        doc.text(pageLabel, pageWidth / 2, pageHeight - 5, { align: "center" });
        if (cfg.watermark) {
          drawPdfWatermark(doc, cfg.watermark, pageWidth, pageHeight);
        }
      }

      doc.save(finalFileName);
      return Promise.resolve();
    });
  },
  drawExportWatermark(ctx, width, height) {
    if (!localStorage.access_token) {
      return;
    }
    const tokenInfoMap = commonFunc.decodeBase64ToUTF8(localStorage.access_token);
    const ipAddress = String(tokenInfoMap.ipAddress || "")
      .split(":")
      .pop();
    const text = `${tokenInfoMap.UserName || "Watermark"}-${new Date().toLocaleString()}-${ipAddress}`;
    const stepX = 380;
    const stepY = 150;
    const diagonal = Math.sqrt(width * width + height * height);
    const columns = Math.ceil(diagonal / stepX) + 1;
    const rows = Math.ceil(diagonal / stepY) + 1;
    ctx.save();
    ctx.globalAlpha = 0.14;
    ctx.fillStyle = "#1F2A44";
    ctx.font = "600 15px 'Plus Jakarta Sans', sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.translate(width / 2, height / 2);
    ctx.rotate(-Math.PI / 6);
    Array.from({ length: rows * columns }).forEach((_, cellIndex) => {
      const row = Math.floor(cellIndex / columns);
      const column = cellIndex % columns;
      const offset = row % 2 === 0 ? 0 : stepX / 2;
      ctx.fillText(text, -diagonal / 2 + column * stepX + offset, -diagonal / 2 + row * stepY);
    });
    ctx.restore();
  },
};
export default commonFunc;
